'use client';
import React, { useEffect, useRef, useState } from 'react';

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
  };

  const contactInfo = [
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      ),
      label: 'Address',
      value: 'Warehouse No.8, 5A And 6A 183/1 2 And 4 Kadaiya, Nani Daman, District-Daman, State – Daman & Diu, India, Pincode 396210',
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.6 1.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.13 6.13l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
      ),
      label: 'Phone',
      value: '+91 8238831338',
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      ),
      label: 'Email',
      value: 'info.bhargavengg@gmail.com',
    },
    {
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
          <polyline points="22,6 12,13 2,6"/>
        </svg>
      ),
      label: 'Business Email',
      value: 'bhargavengg2012@gmail.com',
    },
  ];

  return (
    <section
      id="contact"
      className="py-20"
      style={{ backgroundColor: '#062543', fontFamily: 'Ubuntu, sans-serif' }}
      ref={ref}
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-[#00AAE2] text-[15px] font-medium tracking-[2px] uppercase mb-3">Get In Touch</p>
          <h2
            className="text-white font-medium mb-4"
            style={{ fontSize: '42px', lineHeight: '52px', fontFamily: 'Ubuntu, sans-serif' }}
          >
            Contact Bhargav Parking
          </h2>
          <div className="w-16 h-1 bg-[#00AAE2] mx-auto" />
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Left: Contact Info */}
          <div
            className={`w-full lg:w-5/12 transition-all duration-700 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <h3
              className="text-white font-medium mb-6"
              style={{ fontSize: '26px', lineHeight: '36px', fontFamily: 'Ubuntu, sans-serif' }}
            >
              Let\'s Discuss Your Parking Requirements
            </h3>
            <p
              className="text-white/70 mb-8"
              style={{ fontSize: '16px', lineHeight: '26px', fontFamily: 'Ubuntu, sans-serif' }}
            >
              Whether you need a simple stack parking system or a complex robotic solution, our team of experts is ready to help you find the perfect fit for your project.
            </p>

            <div className="flex flex-col gap-5">
              {contactInfo.map((info, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-full bg-[#00AAE2]/20 flex items-center justify-center text-[#00AAE2] flex-shrink-0 mt-0.5">
                    {info.icon}
                  </div>
                  <div>
                    <div
                      className="text-white/50 text-[13px] mb-1"
                      style={{ fontFamily: 'Ubuntu, sans-serif' }}
                    >
                      {info.label}
                    </div>
                    <div
                      className="text-white"
                      style={{ fontSize: '15px', lineHeight: '22px', fontFamily: 'Ubuntu, sans-serif' }}
                    >
                      {info.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="mt-8">
              <p className="text-white/50 text-[13px] mb-3" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Follow Us</p>
              <div className="flex gap-3">
                {['Facebook', 'LinkedIn', 'Instagram'].map((social) => (
                  <a
                    key={social}
                    href="#"
                    className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:border-[#00AAE2] hover:text-[#00AAE2] transition-all duration-300"
                    aria-label={social}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      {social === 'Facebook' && <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />}
                      {social === 'LinkedIn' && <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z M4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />}
                      {social === 'Instagram' && <><rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" fill="none" stroke="currentColor" strokeWidth="2"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2"/></>}
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className={`w-full lg:w-7/12 transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="bg-white rounded-[20px] p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#00AAE2]/10 flex items-center justify-center mx-auto mb-4">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00AAE2" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <h3 className="text-[#062543] font-medium text-[24px] mb-2" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Enquiry Sent!</h3>
                  <p className="text-[#3A3A3A]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>We\'ll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <h3
                    className="text-[#062543] font-medium mb-2"
                    style={{ fontSize: '22px', fontFamily: 'Ubuntu, sans-serif' }}
                  >
                    Send an Enquiry
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#3A3A3A] text-[14px] mb-1.5" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your full name"
                        className="w-full px-4 py-3 border border-gray-200 rounded-[10px] text-[15px] text-[#231F20] focus:outline-none focus:border-[#00AAE2] transition-colors"
                        style={{ fontFamily: 'Ubuntu, sans-serif' }}
                      />
                    </div>
                    <div>
                      <label className="block text-[#3A3A3A] text-[14px] mb-1.5" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full px-4 py-3 border border-gray-200 rounded-[10px] text-[15px] text-[#231F20] focus:outline-none focus:border-[#00AAE2] transition-colors"
                        style={{ fontFamily: 'Ubuntu, sans-serif' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#3A3A3A] text-[14px] mb-1.5" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 border border-gray-200 rounded-[10px] text-[15px] text-[#231F20] focus:outline-none focus:border-[#00AAE2] transition-colors"
                      style={{ fontFamily: 'Ubuntu, sans-serif' }}
                    />
                  </div>

                  <div>
                    <label className="block text-[#3A3A3A] text-[14px] mb-1.5" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Parking System Type</label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-[10px] text-[15px] text-[#231F20] focus:outline-none focus:border-[#00AAE2] transition-colors bg-white"
                      style={{ fontFamily: 'Ubuntu, sans-serif' }}
                    >
                      <option value="">Select a parking system</option>
                      <option value="stack">Stack Parking System</option>
                      <option value="puzzle">Puzzle Parking System</option>
                      <option value="tower">Tower Parking System</option>
                      <option value="robotic">Shuttle / Robotic Parking</option>
                      <option value="rotary">Rotary Parking System</option>
                      <option value="horizontal">Horizontal Circulation</option>
                      <option value="twowheeler">Two Wheeler Parking</option>
                      <option value="other">Not Sure / Need Consultation</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#3A3A3A] text-[14px] mb-1.5" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Project Details</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Tell us about your project — location, number of cars needed, plot dimensions, etc."
                      className="w-full px-4 py-3 border border-gray-200 rounded-[10px] text-[15px] text-[#231F20] focus:outline-none focus:border-[#00AAE2] transition-colors resize-none"
                      style={{ fontFamily: 'Ubuntu, sans-serif' }}
                    />
                  </div>

                  <div style={{ display: 'inline-block', transform: 'skew(-15deg, 0deg)' }}>
                    <button
                      type="submit"
                      className="relative overflow-hidden bg-[#062543] border border-[#062543] group w-full"
                      style={{ padding: '1px', transition: '0.5s' }}
                    >
                      <span
                        className="relative z-10 block px-8 py-3.5 text-white text-[17px] font-medium text-center"
                        style={{ transform: 'skew(15deg, 0deg)', fontFamily: 'Ubuntu, sans-serif', transition: '0.5s' }}
                      >
                        Send Enquiry
                      </span>
                      <span
                        className="absolute rounded-full bg-[#00AAE2] group-hover:scale-[15] transition-transform duration-700"
                        style={{ width: '10px', height: '10px', top: '50%', left: '50%', transform: 'translate(-50%, -50%) scale(0)', transformOrigin: 'center' }}
                      />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
